import type { SystemUser, SystemUserRole } from '@engagement-web-app/common';
import { numberSchema, stringSchema } from '@engagement-web-app/common/validation-utils';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, stripSearchParams } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { CircleOff, Plus } from 'lucide-react';
import { useCallback, useState } from 'react';
import { z } from 'zod';
import { useSystemAuthentication } from '@/auth/SystemAuthenticationProvider';
import { Button } from '@/components/Button';
import { Dialog, DialogButtonGroup, DialogClose } from '@/components/Dialog';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { InvalidRequest } from '@/components/InvalidRequest';
import { MfaResetDialog } from '@/components/MfaResetDialog';
import { Page } from '@/components/Page';
import { Pagination } from '@/components/Pagination';
import { Pill } from '@/components/Pill';
import { ResetPasswordDialog } from '@/components/ResetPasswordDialog';
import { SearchInput } from '@/components/SearchInput';
import { Skeleton } from '@/components/Skeleton';
import { type AddSystemUserFormData, SystemUserFormDialog } from '@/components/SystemUserFormDialog';
import { EmbeddedWarningToast, showErrorToast, showSuccessToast } from '@/components/Toasts';
import { UserCard, UserCardSkeleton } from '@/components/UserCard';
import { useSystemPermission } from '@/permissions/SystemPermissionProvider';
import { createSystemUser, fetchSystemUserRoles, fetchSystemUsers } from '@/queries/system-users';
import { debounce, getFullName } from '@/utils';
import { useDeleteSystemUser, useEditSystemUser, useMfaReset, usePasswordReset } from './hooks';
import * as styles from './index.css';

// Default values for pagination and search
const FROM = 0;
const LIMIT = 6;
const SEARCH = '';

const GetSystemUserListRequestQuery = z.object({
    from: numberSchema.default(FROM),
    limit: numberSchema.max(100).default(LIMIT),
    search: stringSchema.default(SEARCH),
});

export const Route = createFileRoute('/system/_auth/users/')({
    component: SystemUserListRoute,
    validateSearch: zodValidator(GetSystemUserListRequestQuery),
    search: {
        middlewares: [stripSearchParams({ from: FROM, limit: LIMIT, search: SEARCH })],
    },
    errorComponent: () => <InvalidRequest title="コンサルタント" kind="system" />,
});

function SystemUserListRoute() {
    const currentUser = useSystemAuthentication();
    const { from = FROM, limit = LIMIT, search = SEARCH } = Route.useSearch();
    const [lastPage, setLastPage] = useState<number | null>(null);
    const navigate = Route.useNavigate();
    const queryClient = useQueryClient();
    const { can } = useSystemPermission();
    const [searchText, setSearchText] = useState(search);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

    const debouncedSearch = useCallback(
        debounce((search: string) => {
            navigate({
                from: Route.fullPath,
                search: { from: 0, search },
            });
        }, 300),
        []
    );

    const { isFetching, isError, refetch, data } = useQuery({
        queryKey: ['systemUsers', from, limit, search],
        queryFn: async () => {
            const data = await fetchSystemUsers({ from, limit, search });
            const lastPage = Math.ceil(data.total / limit);
            setLastPage(lastPage);
            return data;
        },
        placeholderData: keepPreviousData,
    });

    const { data: userRoles } = useQuery({
        queryKey: ['systemUserRoles'],
        queryFn: () => fetchSystemUserRoles(),
    });

    const createMutation = useMutation({
        mutationFn: createSystemUser,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['systemUsers'] });
            setIsAddDialogOpen(false);
            showSuccessToast({ title: 'ユーザー追加', description: 'ユーザーが追加されました。' });
        },
        onError: (error) => {
            console.error(error);
            showErrorToast({ title: 'ユーザー追加', description: 'ユーザーの追加に失敗しました。' });
        },
    });

    const handleAddUser = async (formData: AddSystemUserFormData) => {
        await createMutation.mutateAsync({
            lastName: formData.lastName,
            firstName: formData.firstName,
            email: formData.email,
            phoneNumber: formData.phoneNumber,
            roleId: formData.roleId,
        });
    };

    const handlePageChange = (page: number) => {
        navigate({
            from: Route.fullPath,
            search: { from: (page - 1) * limit },
        });
    };

    return (
        <Page.Root>
            <Page.Header title="コンサルタント">
                <Pill>{`${data?.total ?? 0}人`}</Pill>
            </Page.Header>

            <Page.Content>
                <div className={styles.searchRow}>
                    <div className={styles.searchContainer}>
                        <SearchInput
                            placeholder="検索"
                            value={searchText}
                            onChange={(e) => {
                                setSearchText(e.target.value);
                                debouncedSearch(e.target.value);
                            }}
                        />
                    </div>
                    {can('systemUsers', 'create') && (
                        <Button variant="primary" onClick={() => setIsAddDialogOpen(true)}>
                            <Icon icon={Plus} />
                            ユーザーを追加
                        </Button>
                    )}
                </div>

                <ErrorOverlay active={isError} onRetry={refetch}>
                    {!data && <SystemUserListSkeleton />}

                    {data && (
                        <>
                            <p className={styles.countText}>ユーザー数：{data.count}人</p>

                            <Skeleton.Overlay active={isFetching}>
                                <SystemUserList
                                    userRoles={userRoles?.list ?? []}
                                    list={data.list}
                                    currentUserId={currentUser.uuid}
                                />
                            </Skeleton.Overlay>
                        </>
                    )}
                </ErrorOverlay>

                {lastPage && (
                    <Flex justify="center">
                        <Pagination
                            currentPage={from / limit + 1}
                            lastPage={lastPage}
                            onPageChange={handlePageChange}
                        />
                    </Flex>
                )}

                <SystemUserFormDialog
                    mode="add"
                    userRoles={userRoles?.list ?? []}
                    isOpen={isAddDialogOpen}
                    onOpenChange={setIsAddDialogOpen}
                    isSubmitting={createMutation.isPending}
                    onSubmit={handleAddUser}
                />
            </Page.Content>
        </Page.Root>
    );
}

interface SystemUserListProps {
    list: SystemUser[];
    userRoles: SystemUserRole[];
    currentUserId?: string;
}

function SystemUserList({ list, userRoles, currentUserId }: SystemUserListProps) {
    const { editTarget, setEditTarget, handleEditUser, onClickEdit, isPending: isUpdating } = useEditSystemUser();
    const {
        resetPasswordTarget,
        setResetPasswordTarget,
        handleResetPassword,
        onClickResetPassword,
        isPending: isResettingPassword,
    } = usePasswordReset();
    const {
        mfaResetTarget,
        setMfaResetTarget,
        handleResetMfa,
        onClickResetMfa,
        isPending: isResettingMfa,
    } = useMfaReset();
    const {
        deleteTarget,
        setDeleteTarget,
        handleDeleteUser,
        onClickDelete,
        isPending: isDeleting,
    } = useDeleteSystemUser();

    if (list.length === 0) {
        return (
            <div>
                <p className={styles.emptyState}>検索条件に一致するコンサルタントはいません。</p>
            </div>
        );
    }

    return (
        <>
            <div className={styles.cardGrid}>
                {list.map((user) => (
                    <UserCard
                        key={user.uuid}
                        name={getFullName(user)}
                        email={user.email}
                        phoneNumber={user.phoneNumber}
                        role={user.role.name}
                        hideDelete={user.uuid === currentUserId || !user.isActive}
                        isActive={user.isActive}
                        onEdit={onClickEdit(user)}
                        onResetPassword={onClickResetPassword(user)}
                        onResetMfa={onClickResetMfa(user)}
                        onDelete={onClickDelete(user)}
                    />
                ))}
            </div>

            <SystemUserFormDialog
                mode="edit"
                userRoles={userRoles}
                user={editTarget}
                isOpen={editTarget !== null}
                onOpenChange={(open) => !open && setEditTarget(null)}
                isSubmitting={isUpdating}
                onSubmit={handleEditUser}
            />

            <Dialog
                open={deleteTarget !== null}
                onOpenChange={(open) => !open && setDeleteTarget(null)}
                title="コンサルタントのアカウントを無効化にする"
            >
                <EmbeddedWarningToast
                    title="注意です"
                    message={[
                        '・コンサルタントのアカウントを無効化にするとアカウントへのアクセスができなくなります',
                        '・将来に、このアカウントをまた有効にしたい場合は IT サポートまでご連絡ください',
                    ]}
                />
                <p>
                    <strong className={styles.deleteTargetName}>{deleteTarget?.name}</strong>{' '}
                    のコンサルタントアカウントを無効化にしますか？
                </p>

                <DialogButtonGroup>
                    <DialogClose>
                        <Button variant="secondary" disabled={isDeleting}>
                            キャンセル
                        </Button>
                    </DialogClose>
                    <Button variant="danger" onClick={handleDeleteUser} disabled={isDeleting}>
                        {isDeleting ? (
                            '無効化中...'
                        ) : (
                            <>
                                <Icon icon={CircleOff} size="sm" />
                                <span>無効化にする</span>
                            </>
                        )}
                    </Button>
                </DialogButtonGroup>
            </Dialog>

            <ResetPasswordDialog
                target={resetPasswordTarget}
                onOpenChange={(open) => !open && setResetPasswordTarget(null)}
                isSubmitting={isResettingPassword}
                onSubmit={handleResetPassword}
            />

            <MfaResetDialog
                target={mfaResetTarget}
                onOpenChange={(open) => !open && setMfaResetTarget(null)}
                isSubmitting={isResettingMfa}
                onSubmit={handleResetMfa}
            />
        </>
    );
}

function SystemUserListSkeleton() {
    return (
        <>
            <Skeleton.Line width={100} height="xs" />

            <div className={styles.cardGrid}>
                {Array.from({ length: LIMIT }).map((_, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: loading placeholder, cards have no identity
                    <UserCardSkeleton key={i} />
                ))}
            </div>
        </>
    );
}
